import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  MilestoneResponseSchema,
  PullRequestMilestoneSchema,
  type MilestoneResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/milestone'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'

import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {pullRequestsOverviewRoute} from '../routes/overview-route'

export type MutationData =
  /** Sets the pull request's milestone, replacing any it already has. */
  | {action: 'add'; milestone: MilestonePickerMilestone}
  /** Clears the pull request's milestone; `milestoneId` is the milestone's database id. */
  | {action: 'remove'; milestoneId: string}

type Callbacks = {
  onError?: (error: Error) => void
}

export function useModifyMilestone({onError}: Callbacks = {}) {
  const milestoneQueryKey = useRouteQueryKey(pullRequestsOverviewRoute, 'milestone')
  const addMilestoneUrl = usePageDataUrl(PageData.addMilestone)
  const removeMilestoneUrl = usePageDataUrl(PageData.removeMilestone)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response =
        data.action === 'add'
          ? await fetchWithErrorHandling(addMilestoneUrl, {
              method: 'POST',
              headers: {Accept: 'application/json'},
              // The picker is GraphQL-backed, so this is the milestone's relay node id.
              body: {milestoneId: data.milestone.id},
            })
          : await fetchWithErrorHandling(removeMilestoneUrl, {
              method: 'DELETE',
              headers: {Accept: 'application/json'},
              body: {milestoneId: data.milestoneId},
            })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },
    onMutate: async data => {
      if (!milestoneQueryKey) return

      await queryClient.cancelQueries({queryKey: milestoneQueryKey})
      const previous = queryClient.getQueryData<MilestoneResponse>(milestoneQueryKey)

      queryClient.setQueryData<MilestoneResponse>(milestoneQueryKey, () =>
        create(MilestoneResponseSchema, {
          viewerCanSetMilestone: previous?.viewerCanSetMilestone ?? true,
          milestone:
            data.action === 'add'
              ? create(PullRequestMilestoneSchema, {
                  // The database id and number are only known once the server responds; refetching
                  // on settle (below) replaces these placeholders with the authoritative values.
                  id: 0n,
                  nodeId: data.milestone.id,
                  title: data.milestone.title,
                  url: data.milestone.url,
                  closed: data.milestone.closed,
                  closedAt: data.milestone.closedAt ?? '',
                  dueOn: data.milestone.dueOn ?? '',
                  progressPercentage: data.milestone.progressPercentage,
                })
              : undefined,
        }),
      )

      return {previous}
    },
    onError: (e: Error, _vars, onMutateResult) => {
      if (milestoneQueryKey && onMutateResult?.previous) {
        queryClient.setQueryData(milestoneQueryKey, onMutateResult.previous)
      }
      onError?.(e)
    },
    onSettled: () => {
      if (!milestoneQueryKey) return
      return queryClient.invalidateQueries({queryKey: milestoneQueryKey})
    },
  })
}
